import {useState, useMemo, useEffect, useCallback, useRef, FormEventHandler, Dispatch, SetStateAction} from 'react';
import {styled} from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import {FilterDialogSection, FilterDialogSectionId} from 'filters/features/FilterDialog/types';
import {PageFilter} from 'filters/types';

import {useFilterDialogContext} from 'filters/features/FilterDialog/contexts/FilterDialogProvider/FilterDialogProvider';
import {useIsUpSm} from 'utils/hooks/useMediaQuery';
import {SectionSelector} from 'filters/features/FilterDialog/sections/SectionSelector';
import {ActiveFiltersSection} from 'filters/features/FilterDialog/sections/ActiveFiltersSection';
import {dialogEmitter} from 'filters/features/FilterDialog/emitter';
import {ItemsSection} from 'filters/features/FilterDialog/sections/ItemsSection';
import {neverCase} from 'utils/misc';
import {TreeViewSection} from 'filters/features/FilterDialog/sections/TreeViewSection';
import {RecentFiltersSection} from 'filters/features/FilterDialog/sections/RecentFiltersSection';
import {SavedFiltersSection} from 'filters/features/FilterDialog/sections/SavedFiltersSection';
import {FilterDialogActions} from 'filters/features/FilterDialog/sections/FilterDialogActions';
import {confirmation} from 'utils/confirmation';
import {useSourceFilter} from 'filters/contexts/FilterStackProvider';
import {EditedFilterProvider} from 'filters/features/FilterDialog/contexts/internal/EditedFilterProvider';
import {ValidationProvider} from 'filters/features/FilterDialog/contexts/internal/ValidationProvider';
import {useFilterDialogValidator} from 'filters/features/FilterDialog/contexts/internal/ValidationProvider/useFilterDialogValidator';
import {useValidationContext} from 'filters/features/FilterDialog/contexts/internal/ValidationProvider/ValidationProvider';
import {clearStoredId} from 'filters/utils';
import {useFilterActionsContext} from 'filters/contexts/FilterActionsProvider/FilterActionsProvider';

import LoadingIndicator from 'components/LoadingIndicator';

import {DecisionsDialog, DecisionsDialogContent} from 'modules/decisionsNext/components/DecisionsDialog';
import {isEqualFilters} from 'modules/decisionsNext/components/BaseFilter/utils/filter';

export const SFilterSection = styled('div')(({theme}) => ({
  overflowY: 'auto',
  minHeight: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),

  '> :last-child': {
    flex: '1',
    background: theme.palette.common.white,
    borderRadius: theme.shape.borderRadiusRegular,
  },
}));

export const FilterSectionRenderer = ({section}: {section?: FilterDialogSection}) => {
  if (!section) {
    return <div>Section not defined</div>;
  }

  switch (section.type) {
    case 'active-filters':
      return <ActiveFiltersSection {...section} />;
    case 'recent-filters':
      return <RecentFiltersSection {...section} />;
    case 'saved-filters':
      return <SavedFiltersSection {...section} />;
    case 'tree-view':
      return <TreeViewSection {...section} />;
    case 'items':
      return <ItemsSection {...section} />;
    default:
      neverCase(section);
  }
};

const SDecisionsDialogContent = styled(DecisionsDialogContent)(({theme}) => ({
  backgroundColor: theme.palette.background.grey,

  [theme.breakpoints.up('sm')]: {
    display: 'grid',
    gridTemplateColumns: '206px 1fr',
    gap: theme.spacing(3),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
}));

const Dialog = () => {
  const {id, title, sections} = useFilterDialogContext();
  const {useApplyFilter} = useFilterActionsContext();
  const onApply = useApplyFilter();

  const isUpSm = useIsUpSm();
  const formRef = useRef<HTMLFormElement>(null);

  const [open, setOpen] = useState(false);
  const [sectionSelectorOpen, setSectionSelectorOpen] = useState(false);
  const [sectionId, setSectionId] = useState(() => sections[0]?.id);
  const contentRef = useRef<HTMLDivElement>(null);

  const source = useSourceFilter();
  const clearedSource = useMemo(() => (source ? clearStoredId(source) : undefined), [source]);

  const [filter, setFilter] = useState<PageFilter>();
  const isDirty = useMemo(
    () => (!clearedSource || !filter ? false : !isEqualFilters(filter, clearedSource)),
    [filter, clearedSource]
  );

  useEffect(() => {
    setFilter(clearedSource);
  }, [clearedSource]);

  const {formError, fieldErrors} = useValidationContext();
  useFilterDialogValidator(filter, open);

  const section = useMemo(() => sections.find(section => section.id === sectionId), [sections, sectionId]);

  useEffect(() => {
    const event = `open/${id}` as const;

    dialogEmitter.on(event, section => {
      if (!id && dialogEmitter.listenerCount(event) > 1) {
        throw new Error(
          'Multiple global FilterDialogs detected. Please provide a unique id prop to each FilterDialog instance.'
        );
      }

      setOpen(true);
      section && setSectionId(section);
      setSectionSelectorOpen(false);
    });

    return () => {
      dialogEmitter.removeListener(event);
    };
  }, [id]);

  const onSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    e => {
      if (!filter) {
        throw new Error('Filter is not defined');
      }

      e.preventDefault();
      e.stopPropagation();

      const isValid = !formError && Object.keys(fieldErrors).length === 0;

      if (isValid) {
        setOpen(false);

        return onApply(filter);
      }

      const field = Object.keys(fieldErrors).at(0);

      if (!field) {
        return;
      }

      const isItem = field.includes('/');
      const sectionId = isItem ? field.split('/')[0] : field;

      setSectionId(sectionId as FilterDialogSectionId);

      if (isItem) {
        requestAnimationFrame(() => {
          const item = field.split('/')[1];

          contentRef.current
            ?.querySelector(`[data-item-id="${item}"]`)
            ?.scrollIntoView({behavior: 'smooth', block: 'nearest'});
        });
      }
    },
    [fieldErrors, filter, formError, onApply]
  );

  const handleClose = useCallback(async () => {
    if (!isDirty) {
      return setOpen(false);
    }

    try {
      await confirmation({
        content: (
          <>
            You have unsaved filters selected.
            <br />
            Are you sure you want to close the dialog?
          </>
        ),
        cancelButtonText: 'Discard',
        confirmButtonText: 'Apply filters',
      });
      formRef.current?.requestSubmit();
    } catch {
      setOpen(false);
    }
  }, [isDirty]);

  return (
    <DecisionsDialog
      maxWidth='md'
      dialogHeight='min(calc(100% - 64px), 800px)'
      open={open}
      title={title}
      onExited={() => {
        setFilter(clearedSource);
        setSectionId(sections[0].id);
      }}
      onClose={handleClose}
      onBack={!isUpSm && !sectionSelectorOpen ? () => setSectionSelectorOpen(true) : undefined}
      fixedSwiperHeight
    >
      {!filter && <LoadingIndicator />}
      {filter && (
        <EditedFilterProvider
          filter={filter}
          setFilter={setFilter as Dispatch<SetStateAction<PageFilter>>}
          isDirty={isDirty}
        >
          <SDecisionsDialogContent ref={contentRef}>
            {(sectionSelectorOpen || isUpSm) && (
              <SectionSelector
                activeId={sectionId}
                setActiveId={sectionId => {
                  setSectionId(sectionId);
                  setSectionSelectorOpen(false);
                }}
              />
            )}
            {(!sectionSelectorOpen || isUpSm) && (
              <SFilterSection>
                {!isUpSm && (
                  <Typography variant='body1Bold' marginInline={4}>
                    {section?.title}
                  </Typography>
                )}
                <FilterSectionRenderer key={section?.id} section={section} />
              </SFilterSection>
            )}
          </SDecisionsDialogContent>
          <form ref={formRef} onSubmit={onSubmit}>
            <FilterDialogActions section={section} onClose={handleClose} />
          </form>
        </EditedFilterProvider>
      )}
    </DecisionsDialog>
  );
};

export const FilterDialog = () => {
  return (
    <ValidationProvider>
      <Dialog />
    </ValidationProvider>
  );
};
